import { getGetAllUrl } from 'generatedTypes/asset/services/asset-controller';
import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import usePagination from 'hooks/usePagination';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { DEFAULT_PAGE_SIZE } from './constants';
import type { AssetResponse, FetchAssets, GetAllParams, PagedResponseAssetResponse } from './types';

const useAssets = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR } = useTranslation();

    const fetchAssets: FetchAssets = async requestOptions => {
        try {
            setIsLoading(true);
            const url = getGetAllUrl(requestOptions);

            return await withRetry(
                () => authenticatedGet<PagedResponseAssetResponse>(url),
                CONNECTION_ERROR,
                () => fetchAssets(requestOptions)
            );
        } finally {
            setIsLoading(false);
        }
    };

    const { data, fetch: paginatedFetch } = usePagination<AssetResponse>({
        callback: fetchAssets
    });

    const handleFetch = (args?: GetAllParams) => {
        const { pageSize = DEFAULT_PAGE_SIZE, sort } = args || {};

        return paginatedFetch({
            pageSize,
            sort
        });
    };

    return {
        assets: data as AssetResponse[],
        fetch: handleFetch,
        isLoading
    };
};

export default useAssets;
